import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Tag } from 'lucide-react';
import type { ModelToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useModelsList, useModelMutations } from '~/hooks/use-models';
import { tokenToForm, type ModelFormValues } from '~/lib/schemas/model';
import { ModelEditDialog } from './model-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Models (Μοντέλα) CRUD surface — parity with the legacy admin grid (Name/Visible/Sort/ERP Code). */
export function ModelsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useModelsList();
	const { remove } = useModelMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<ModelFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<ModelToken | null>(null);

	const columns = useMemo<SystemGridColumn<ModelToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.models.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'erpCode',
				header: t('catalog.models.columns.erpCode'),
				width: '220px',
				sortValue: o => o.erpcd ?? '',
				exportValue: o => o.erpcd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('catalog.models.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.models.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ModelToken>
				gridId="models"
				title={t('catalog.models.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.erpcd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Tag size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.models.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="models"
			/>

			<ModelEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.models.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.models.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
